import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { Employee } from "../../models/employee.interface";
import WeeklyCalendar from "../../components/calendars/weekly-calendar/WeeklyCalendar";
import MonthBar from "../../components/calendars/shift-calendar/MonthBar";
import WeekStats from "../../components/calendars/shift-calendar/WeekStats";
import WeekShiftItem from "../../components/calendars/shift-calendar/WeekShiftItem";
import { retrieveShiftsWeekStats } from "../../services/data-manager/data-manager.service";
import { getMonthlyCalendar } from "../../services/backend/api/shifts/shifts.apis";
import { MonthlyCalendarRequest } from "../../services/backend/api/shifts/requests/monthly-calendar-request";
import { WeekData, DateCalendarData } from "../../services/backend/api/shifts/responses/monthly-calendar-response";
import { ShiftsWeekStatsRequest } from "../../services/backend/api/shifts/requests/shifts-week-stats.request";
import { ShiftsWeekStatsResponse } from "../../services/backend/api/shifts/responses/shifts-week-stats.response";
import { Shift } from "../../models/shift.interface";
import Loader from "../../components/common/LoaderComponent";
import { useToastContext } from "../../contexts/ToastContext";
import { HiOutlineRefresh } from "react-icons/hi";

const ShiftAssignmentPage: React.FC = () => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [isMonthStatsLoading, setIsMonthStatsLoading] = useState(true);

    const [monthShiftsData, setMonthShiftsData] = useState<WeekData[]>([]);
    const [filteredMonthShiftsData, setFilteredMonthShiftsData] = useState<WeekData[]>([]);
    const [availableEmployees, setAvailableEmployees] = useState<Employee[]>([]);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [weekStats, setWeekStats] = useState<ShiftsWeekStatsResponse>();
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [expandedWeeks, setExpandedWeeks] = useState<Record<number, boolean>>({});
    // Stato per mese e anno correnti
    const today = new Date();
    const [currentMonth, setCurrentMonth] = useState<number>(today.getMonth());
    const [currentYear, setCurrentYear] = useState<number>(today.getFullYear());
    const { successToast, errorToast } = useToastContext();

    const getMonthRange = (monthData: WeekData[]): string => {
        if (!monthData || monthData.length === 0) return '';

        const firstWeek = monthData[0];
        const lastWeek = monthData[monthData.length - 1];
        const firstDate = new Date(firstWeek.week[0].date);
        const lastDate = new Date(lastWeek.week[lastWeek.week.length - 1].date);

        return t('shiftAssignmentPage.dateFormats.monthRange', {
            start: firstDate.getDate(),
            end: lastDate.getDate(),
            month: (firstDate.getMonth() + 1).toString().padStart(2, '0'),
            year: firstDate.getFullYear()
        });
    };

    const onShiftsChange = (shifts: Shift[]) => {
        setShifts(shifts);
    }

    // Funzione per ricaricare i dati del mese quando viene salvato un turno
    const loadMonthStats = () => {
        if (currentMonth === undefined || currentYear === undefined) {
            return;
        }
        setIsMonthStatsLoading(true);
        const request: MonthlyCalendarRequest = {
            monthIndex: currentMonth,
            year: currentYear
        };
        getMonthlyCalendar(request).then((response) => {
            setMonthShiftsData(response.data);
            setIsMonthStatsLoading(false);
        }).catch((error) => {
            console.error("Errore nel ricaricare i dati per le statistiche mensili", error);
            setIsMonthStatsLoading(false);
        });
    };

    useEffect(() => {
        const filteredData = monthShiftsData.filter(weekData => {
            return true;
        });

        setFilteredMonthShiftsData(filteredData);
    }, [monthShiftsData]);

    useEffect(() => {
        setIsMonthStatsLoading(true);
        if (currentMonth === undefined || currentYear === undefined) {
            return;
        }
        const request: MonthlyCalendarRequest = {
            monthIndex: currentMonth,
            year: currentYear
        };
        loadMonthStats();
    }, [currentMonth, currentYear]);

    useEffect(() => {
        console.log("Cambiato giorno, sto prendendo i dati per le statistiche settimanali")
        const request: ShiftsWeekStatsRequest = {
            date: selectedDate
        };
        setIsLoading(true);
        retrieveShiftsWeekStats(request).then((response) => {
            // This permits the progress bar to be displayed even if the total worked hours are 0
            if (!response.week.worked || response.week.worked <= 0) {
                response.week.worked = 1;
            }
            if (!response.month.worked || response.month.worked <= 0) {
                response.month.worked = 1;
            }
            setWeekStats(response);
            setIsLoading(false);
        }).catch((error) => {
            console.error("Errore nel prendere i dati per le statistiche settimanali", error);
            setIsLoading(false);
        });
    }, [selectedDate]);

    // Funzione per formattare il range settimanale (06/02 - 06/08)
    const getWeekRangeLabel = (week: DateCalendarData[]): string => {
        if (!week || week.length === 0) return '';

        const first = new Date(week[0].date);
        const last = new Date(week[week.length - 1].date);

        return t('shiftAssignmentPage.dateFormats.weekRange', {
            startDay: first.getDate().toString().padStart(2, '0'),
            startMonth: (first.getMonth() + 1).toString().padStart(2, '0'),
            endDay: last.getDate().toString().padStart(2, '0'),
            endMonth: (last.getMonth() + 1).toString().padStart(2, '0')
        });
    };

    // Funzione per formattare la data completa del giorno
    const formatFullDate = (date: string): string => {
        const dateObj = new Date(date);
        return t('shiftAssignmentPage.dateFormats.fullDate', {
            day: dateObj.getDate(),
            month: t(`shiftAssignmentPage.months.${dateObj.toLocaleString('en-US', { month: 'long' }).toLowerCase()}`),
            year: dateObj.getFullYear()
        });
    };

    // Funzione per calcolare il numero totale di turni per una settimana
    const getWeekShiftsCount = (week: DateCalendarData[]): number => {
        return week.reduce((total, dateData) => {
            return total + dateData.calendar.reduce((dayTotal, calendarItem) => {
                return dayTotal + 1 + calendarItem.operators.length; // 1 supervisor + operators
            }, 0);
        }, 0);
    };

    const formatShiftCount = (count: number): string => {
        return `${count} ${t('shiftAssignmentPage.shifts')}`;
    };

    // Funzione per cambiare mese
    const handleMonthChange = (direction: 'prev' | 'next') => {
        let newMonth = currentMonth;
        let newYear = currentYear;
        if (direction === 'prev') {
            newMonth--;
            if (newMonth < 0) {
                newMonth = 11;
                newYear--;
            }
        } else {
            newMonth++;
            if (newMonth > 11) {
                newMonth = 0;
                newYear++;
            }
        }
        setCurrentMonth(newMonth);
        setCurrentYear(newYear);
        // Aggiorna la selectedDate per mantenere la compatibilità con WeeklyCalendar
        setSelectedDate(new Date(newYear, newMonth, 1));
    };

    const getMonthShiftsCount = (weeks: WeekData[]): number => {
        return weeks.reduce((total, weekData) => {
            return total + getWeekShiftsCount(weekData.week);
        }, 0);
    };

    const toggleAllWeeks = (show: boolean) => {
        const newExpandedWeeks: Record<number, boolean> = {};
        filteredMonthShiftsData.forEach((_, index) => {
            newExpandedWeeks[index] = show;
        });
        setExpandedWeeks(newExpandedWeeks);
    };

    return (
        <div className="page-container mr-12">
            <div className="page-title">
                <h2>{t('shiftAssignmentPage.title')}</h2>
            </div>
            <div className="page-content">
                <div className="flex justify-center items-center w-full mb-4">
                    <WeekStats isLoading={isLoading} weekStats={weekStats} />
                </div>

                <div className='w-full grid grid-cols-8 gap-4 visible-height'>
                    <div className="col-span-3 space-y-4 ">
                        <div className="space-y-4 secondary-container padded-m visible-height overflow-auto">
                            <div className="flex items-center justify-between">
                                <p className="font-semibold text-2xl">
                                    <span className="ml-2">{getMonthRange(monthShiftsData)}</span>
                                </p>
                                {/*Update icon*/}
                                <button onClick={() => loadMonthStats()} className="text-sm text-gray-300 focus:outline-none">
                                    <HiOutlineRefresh size={24} />
                                </button>
                                {/* Barra di navigazione mese */}
                                <div className="flex items-center gap-2">
                                    <button onClick={() => handleMonthChange('prev')} className="text-2xl px-2 py-1 focus:outline-none">&#60;</button>
                                    <span className="font-bold text-xl px-2 select-none">
                                        {t(`shiftAssignmentPage.months.${new Date(currentYear, currentMonth).toLocaleString('en-US', { month: 'long' }).toLowerCase()}`)}
                                    </span>
                                    <button onClick={() => handleMonthChange('next')} className="text-2xl px-2 py-1 focus:outline-none">&#62;</button>
                                </div>
                            </div>

                            {/* Header mensile con conteggio totale e pulsante espandi/chiudi tutto */}
                            {!isMonthStatsLoading && filteredMonthShiftsData.length > 0 && (
                                <div className="ml-2">
                                <MonthBar
                                    currentMonth={currentMonth}
                                    currentYear={currentYear}
                                    monthData={filteredMonthShiftsData}
                                    onToggleAll={toggleAllWeeks}
                                    formatShiftCount={formatShiftCount}
                                        getMonthShiftsCount={getMonthShiftsCount}
                                    />
                                </div>
                            )}

                            <div className="space-y-2 overflow-auto p-2">
                                {
                                    isMonthStatsLoading || !filteredMonthShiftsData ? <Loader /> :
                                        filteredMonthShiftsData.map((weekData, weekIndex) => {
                                            const isExpanded = expandedWeeks[weekIndex] !== false;
                                            return (
                                                <div key={weekIndex} className={isExpanded ? "mb-6" : "mb-2"}>
                                                    {/* Header settimana con range e pulsante espandi/chiudi */}
                                                    <div className="flex items-center justify-between cursor-pointer select-none px-2 py-1  rounded-t-lg" onClick={() => setExpandedWeeks(prev => ({ ...prev, [weekIndex]: !isExpanded }))}>
                                                        <div className="flex items-center gap-3">
                                                            <span className="font-bold text-lg tracking-wide">{getWeekRangeLabel(weekData.week)}</span>

                                                            <span className="text-sm text-white px-2 py-1 rounded">
                                                                {formatShiftCount(getWeekShiftsCount(weekData.week))}
                                                            </span>
                                                        </div>
                                                        <button className="text-sm text-gray-300 focus:outline-none">
                                                            {isExpanded ? t('shiftAssignmentPage.hide') + ' ▲' : t('shiftAssignmentPage.show') + ' ▼'}
                                                        </button>
                                                    </div>
                                                    {/* Giorni della settimana, visibili solo se espansa */}
                                                    {isExpanded && (
                                                        <div className="space-y-3 border-l-2 border-white pl-2 bg-transparent">
                                                            {weekData.week.map((dateData, dateIndex) => (
                                                                <WeekShiftItem
                                                                    key={dateIndex}
                                                                    dateData={dateData}
                                                                    formatFullDate={formatFullDate}
                                                                    formatShiftCount={formatShiftCount}
                                                                    onGoToSpecificDate={(date) => {
                                                                        const selectedDateObj = new Date(date);
                                                                        // Imposta l'ora a 00:00:00 per avere il formato corretto
                                                                        selectedDateObj.setHours(0, 0, 0, 0);
                                                                        console.log("Cambiato giorno -> ", selectedDateObj)
                                                                        setSelectedDate(selectedDateObj)
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                }
                            </div>
                        </div>
                    </div>
                    <div className="col-span-5 secondary-container padded-m visible-height overflow-auto">
                        <WeeklyCalendar
                            onChange={(date) => {
                                console.log("Cambiato giorno")
                                setSelectedDate(date)
                            }}
                            onVisibleShiftsChange={onShiftsChange}
                            onAvailableEmployeesChange={setAvailableEmployees}
                            onSaveShifts={() => loadMonthStats()}
                            selectedDate={selectedDate}
                            onSelectDateChange={(date) => {
                                console.log("Cambiato giorno -> ", date)
                                setSelectedDate(date)
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShiftAssignmentPage;
